import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { applyRowSelection } from '../lib';

const InsightContactContext = createContext(null);

function InsightContactProvider({ scopeKey, children }) {
  const [selectedRows, setSelectedRows] = useState([]);

  useEffect(() => {
    setSelectedRows([]);
  }, [scopeKey]);

  // Row selection for MUI Table
  const rowSelection = useMemo(
    () => Object.fromEntries(selectedRows.map(row => [row.id, true])),
    [selectedRows],
  );

  const addRowSelection = useCallback((rowsToAdd, updater) => {
    let limitReached = false;

    setSelectedRows(previousRows => {
      const result = applyRowSelection({
        selectedRows: previousRows,
        rowsToAdd,
        updater,
      });

      limitReached = result.limitReached;

      return result.rejected ? previousRows : result.selectedRows;
    });

    return { limitReached };
  }, []);

  const removeRowSelection = useCallback(ids => {
    const idSet = new Set(ids);

    setSelectedRows(previousRows =>
      previousRows.filter(row => !idSet.has(row.id)),
    );
  }, []);

  const clearRowSelection = useCallback(() => {
    setSelectedRows([]);
  }, []);

  const value = useMemo(
    () => ({
      selectedRows,
      rowSelection,
      addRowSelection,
      removeRowSelection,
      clearRowSelection,
    }),
    [
      selectedRows,
      rowSelection,
      addRowSelection,
      removeRowSelection,
      clearRowSelection,
    ],
  );

  return (
    <InsightContactContext.Provider value={value}>
      {children}
    </InsightContactContext.Provider>
  );
}

function useInsightContact() {
  const context = useContext(InsightContactContext);
  return context;
}

export { InsightContactProvider, useInsightContact };
